import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker, Polyline } from '@react-google-maps/api';
import ModernPlaceAutocomplete from '../../../../core/common/ModernPlaceAutocomplete';
import { computeRoute, LatLngPoint } from '../../../../core/utils/routeService';
import GlassSelect from '../../../../core/common/glassSelect/GlassSelect';
import { Booking, Location } from '../booking';
import { showSuccessAlert, showError,showCancelConfirmation } from '../../../../core/alert/sweetAlertConfig';
import { useNavigate } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import Swal from 'sweetalert2';

type Library = 'places';

const GOOGLE_MAPS_LIBRARIES: Library[] = ['places'];

interface BookingForm {
  bookingType: string;
  priority: string;
  locations: Location[];
  reason: string;
  noOfPassengers: number;
  departureTime: string;
  arrivalTime: string;
  estimatedKms: number;
  totalWaitingTime: number;
  driverId?: string;
  vehicleId?: string;
}
// Toggle this to control whether locations are editable in this modal
const locationsLocked = true; // <- make them non-clickable / read-only

const center = { lat: 6.9271, lng: 79.8612 };

interface Driver {
  empNumber: string;
  name: string;
}

interface Vehicle {
  numberPlate: string;
  noOfPassengers: string;
}

interface EditVehicleBookingsProps {
  show: boolean;
  onHide: () => void;
  booking: Booking;
  updateVehicleBooking: (bookingId: number, updatedFields: Partial<Booking>) => Promise<boolean>;
  isUpdating: boolean;
}

const formatDateTimeLocal = (timestamp: number | string | Date | undefined): string => {
  if (!timestamp) return '';
  try {
    const date = timestamp instanceof Date ? timestamp : typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  } catch (error) {
    return '';
  }
};

const EditVehicleBookings: React.FC<EditVehicleBookingsProps> = ({
  show,
  onHide,
  booking,
  updateVehicleBooking,
  isUpdating,
}) => {
  const navigate = useNavigate();
  const googleMapsApiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    'AIzaSyBuEcklY7fN-33BDjLDlzRNxNU1uGvPfxk';

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [formData, setFormData] = useState<BookingForm>({
    bookingType: booking.bookingType || 'Planned',
    priority: booking.priority || 'P1',
    locations: booking.locations?.map((loc, index) => ({
      id: loc.id,
      address: loc.address,
      lat: loc.lat?.toString() || '',
      lng: loc.lng?.toString() || '',
      orderId: loc.orderId || index + 1,
      type: index === 0 ? 'start' : index === booking.locations.length - 1 ? 'return' : 'stop',
      status: loc.status || null,
      createdAt: loc.createdAt || null,
    })) || [
      { id: 0, address: '', lat: '', lng: '', orderId: 1, type: 'start', status: null, createdAt: null },
      { id: 0, address: '', lat: '', lng: '', orderId: 2, type: 'stop', status: null, createdAt: null },
      { id: 0, address: '', lat: '', lng: '', orderId: 3, type: 'return', status: null, createdAt: null },
    ],
    reason: booking.reason || '',
    noOfPassengers: booking.noOfPassengers || 1,
    departureTime: formatDateTimeLocal(booking.departureTime),
    arrivalTime: formatDateTimeLocal(booking.arrivalTime),
    estimatedKms: booking.estimatedKms || 0,
    totalWaitingTime: booking.totalWaitingTime || 0,
    driverId: booking.driverId || '',
    vehicleId: booking.vehicleId || '',
  });

  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [routePath, setRoutePath] = useState<LatLngPoint[]>([]);
  const [stopWaitingTimes, setStopWaitingTimes] = useState<{ [key: number]: number }>({});
  const [error, setError] = useState<string | null>(null);
  const [isServiceManager, setIsServiceManager] = useState<boolean>(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState<boolean>(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const autocompleteRefs = useRef<(google.maps.places.Autocomplete | null)[]>([]);
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (!googleMapsApiKey) {
      showError('Google Maps API key is missing. Please contact the administrator.');
    }
  }, [googleMapsApiKey]);

  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate(all_routes.login);
      return false;
    }
    return token;
  };

  useEffect(() => {
    const fetchUserRole = async () => {
      const token = checkToken();
      if (!token) return;

      try {
        setIsServiceManager(localStorage.group === 'SERVICE_MANAGER');
      } catch (err: any) {
        const errorMsg = err.response?.data?.statusDescription || 'Failed to fetch user role.';
        setError(errorMsg);
        Swal.fire({
          icon: 'error',
          title: 'Role Check Error',
          text: errorMsg,
        });
      }
    };

    if (show) {
      fetchUserRole();
    }
  }, [show, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!show || !isServiceManager) return;

      setIsLoadingDrivers(true);
      setIsLoadingVehicles(true);
      setError(null);

      try {
        const departureTime = formData.departureTime
          ? new Date(formData.departureTime).getTime().toString()
          : undefined;
        const arrivalTime = formData.arrivalTime
          ? new Date(formData.arrivalTime).getTime().toString()
          : undefined;

        if (!departureTime) {
          setError('Departure time is required to fetch drivers.');
          Swal.fire({
            icon: 'warning',
            title: 'Missing Departure Time',
            text: 'Departure time is required to fetch available drivers.',
          });
          setIsLoadingDrivers(false);
          return;
        }

        const driverResponse = await apiDefinitions.getAvailableDrivers(departureTime, arrivalTime);
        if (driverResponse.data.status !== 'S1000') {
          const errorMsg = driverResponse.data.statusDescription || 'Failed to fetch drivers.';
          setError(errorMsg);
          Swal.fire({
            icon: 'error',
            title: 'Driver Fetch Error',
            text: errorMsg,
          });
          setIsLoadingDrivers(false);
          return;
        }

        const driverData = driverResponse.data?.data?.drivers || [];
        const mappedDrivers = driverData
          .map((d: any) => ({
            empNumber: d.empNumber.toString(),
            name: d.name || 'Unknown',
          }))
          .filter((d: Driver) => d.empNumber && d.empNumber !== '');
        setDrivers(mappedDrivers);

        const vehicleResponse = await apiDefinitions.getAvailableVehicles(
          departureTime,
          arrivalTime,
          formData.noOfPassengers
        );
        const vehicleData = vehicleResponse.data?.data?.vehicles || [];
        const mappedVehicles = vehicleData
          .map((v: any) => ({
            numberPlate: (v.numberPlate || '').toString(),
            noOfPassengers: v.noOfPassengers || 'N/A',
          }))
          .filter((v: Vehicle) => v.numberPlate);
        setVehicles(mappedVehicles);

        if (mappedDrivers.length === 0) {
          Swal.fire({
            icon: 'warning',
            title: 'No Drivers Available',
            text: 'No drivers are available for the selected time.',
          });
        }
        if (mappedVehicles.length === 0) {
          Swal.fire({
            icon: 'warning',
            title: 'No Vehicles Available',
            text: 'No vehicles are available for the selected time and passenger count.',
          });
        }
      } catch (err: any) {
        const errorMsg = err.response?.data?.statusDescription || 'Failed to fetch drivers or vehicles.';
        setError(errorMsg);
        Swal.fire({
          icon: 'error',
          title: 'Fetch Error',
          text: errorMsg,
        });
      } finally {
        setIsLoadingDrivers(false);
        setIsLoadingVehicles(false);
      }
    };

    fetchData();
  }, [show, isServiceManager, formData.departureTime, formData.arrivalTime, formData.noOfPassengers]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'noOfPassengers' ? parseInt(value) || 1 : value,
    }));
    if (name === 'departureTime' || name === 'noOfPassengers') {
      setDirections(null);
    }
  };

  const handleServiceManagerInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLocationChange = (index: number, field: keyof Location, value: string | number) => {
    setFormData((prev) => {
      const newLocations = [...prev.locations];
      newLocations[index] = { ...newLocations[index], [field]: value };
      return { ...prev, locations: newLocations };
    });
    setDirections(null);
  };

  const handleWaitingTimeChange = (index: number, value: number) => {
    setStopWaitingTimes((prev) => {
      const newWaitingTimes = { ...prev, [index]: value };
      const total = Object.values(newWaitingTimes).reduce((sum, time) => sum + (time || 0), 0);
      setFormData((prevForm) => ({ ...prevForm, totalWaitingTime: total }));
      return newWaitingTimes;
    });
    setDirections(null);
  };

  const addStopLocation = () => {
    setFormData((prev) => {
      const newLocations = [
        ...prev.locations.slice(0, -1),
        {
          id: 0,
          address: '',
          lat: '',
          lng: '',
          orderId: prev.locations.length,
          type: 'stop' as 'stop',
          status: null,
          createdAt: null,
        },
        prev.locations[prev.locations.length - 1],
      ];
      return { ...prev, locations: newLocations };
    });
    autocompleteRefs.current.push(null);
    setDirections(null);
  };

  const removeStopLocation = (index: number) => {
    setFormData((prev) => {
      const newLocations = prev.locations
        .filter((_, i) => i !== index)
        .map((loc, i) => ({ ...loc, orderId: i + 1 }));
      return { ...prev, locations: newLocations };
    });
    setStopWaitingTimes((prev) => {
      const newWaitingTimes = { ...prev };
      delete newWaitingTimes[index];
      const total = Object.values(newWaitingTimes).reduce((sum, time) => sum + (time || 0), 0);
      setFormData((prevForm) => ({ ...prevForm, totalWaitingTime: total }));
      return newWaitingTimes;
    });
    autocompleteRefs.current = autocompleteRefs.current.filter((_, i) => i !== index);
    setDirections(null);
  };

  const onPlaceChanged = (index: number) => {
    const autocomplete = autocompleteRefs.current[index];
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place && place.geometry && place.geometry.location) {
        const address = place.formatted_address || place.name || '';
        const lat = place.geometry.location.lat().toString();
        const lng = place.geometry.location.lng().toString();
        setFormData((prev) => {
          const newLocations = [...prev.locations];
          newLocations[index] = { ...newLocations[index], address, lat, lng };
          return { ...prev, locations: newLocations };
        });
        setDirections(null);
      } else {
        showError('Selected location is invalid. Please choose a valid address from the suggestions.');
      }
    } else {
      showError('Autocomplete not initialized for this location.');
    }
  };

  const debounce = <T extends (...args: any[]) => void>(func: T, wait: number) => {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const routeDurationSecondsRef = useRef<number>(0);

  const calculateRoute = useCallback(async () => {
    if (!isLoaded) {
      return;
    }

    const origin = formData.locations.find((loc) => loc.type === 'start');
    const destination = formData.locations.find((loc) => loc.type === 'return');

    const hasStart = origin?.lat && origin?.lng && !isNaN(parseFloat(origin.lat)) && !isNaN(parseFloat(origin.lng));
    const hasReturn = destination?.lat && destination?.lng && !isNaN(parseFloat(destination.lat)) && !isNaN(parseFloat(destination.lng));

    if (!hasStart || !hasReturn) {
      setRoutePath([]);
      setDirections(null);
      setFormData((prev) => (prev.estimatedKms !== 0 ? { ...prev, estimatedKms: 0 } : prev));
      return;
    }

    const stops = formData.locations.filter((loc) => loc.type === 'stop');
    const waypoints: LatLngPoint[] = stops
      .filter((loc) => loc.lat && loc.lng && !isNaN(parseFloat(loc.lat)) && !isNaN(parseFloat(loc.lng)))
      .map((loc) => ({
        lat: parseFloat(loc.lat),
        lng: parseFloat(loc.lng),
      }));

    try {
      const result = await computeRoute(
        { lat: parseFloat(origin!.lat), lng: parseFloat(origin!.lng) },
        { lat: parseFloat(destination!.lat), lng: parseFloat(destination!.lng) },
        waypoints,
        googleMapsApiKey
      );

      setRoutePath(result.path);
      routeDurationSecondsRef.current = result.durationSeconds;

      const totalWaitingSeconds = (formData.totalWaitingTime || 0) * 60;

      let computedArrivalTime = formData.arrivalTime;
      if (formData.departureTime) {
        const departureDate = new Date(formData.departureTime);
        if (!isNaN(departureDate.getTime())) {
          const arrivalDate = new Date(
            departureDate.getTime() + (result.durationSeconds + totalWaitingSeconds) * 1000
          );
          computedArrivalTime = formatDateTimeLocal(arrivalDate);
        }
      }

      setFormData((prev) => ({
        ...prev,
        estimatedKms: result.estimatedKms,
        ...(computedArrivalTime ? { arrivalTime: computedArrivalTime } : {}),
      }));

      if (mapRef.current && result.path.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        result.path.forEach((point) => bounds.extend(point));
        mapRef.current.fitBounds(bounds);
      }
    } catch (error) {
      console.warn('Failed to calculate route directions:', error);
    }
  }, [isLoaded, formData.locations, formData.departureTime, formData.totalWaitingTime, formData.arrivalTime, googleMapsApiKey]);

  // Automated trigger whenever locations, departure time, or waiting times change
  const locationsKey = formData.locations
    .map((l) => `${l.type}:${l.lat},${l.lng}`)
    .join('|');

  useEffect(() => {
    if (!isLoaded) return;
    const timer = setTimeout(() => {
      calculateRoute();
    }, 400);
  }, [isLoaded, locationsKey, formData.departureTime, formData.totalWaitingTime, calculateRoute]);

  // Sync Arrival Time whenever Departure Time or Waiting Times change
  useEffect(() => {
    if (!formData.departureTime || routeDurationSecondsRef.current <= 0) return;
    const departureDate = new Date(formData.departureTime);
    if (isNaN(departureDate.getTime())) return;

    const totalWaitingSeconds = (formData.totalWaitingTime || 0) * 60;
    const arrivalDate = new Date(
      departureDate.getTime() + (routeDurationSecondsRef.current + totalWaitingSeconds) * 1000
    );
    const computedArrival = formatDateTimeLocal(arrivalDate);

    setFormData((prev) => (prev.arrivalTime !== computedArrival ? { ...prev, arrivalTime: computedArrival } : prev));
  }, [formData.departureTime, formData.totalWaitingTime]);

  const validateTimeDifference = () => {
    const departure = new Date(formData.departureTime).getTime();
    const arrival = new Date(formData.arrivalTime).getTime();
    const diffHours = (arrival - departure) / (1000 * 60 * 60);

    const priorityHours: { [key: string]: number } = {
      P1: 12,
      P2: 24,
      P3: 48,
    };

    const maxHours = priorityHours[formData.priority];
    if (diffHours > maxHours) {
      showError(`The time difference for ${formData.priority} priority should not exceed ${maxHours} hours.`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateTimeDifference()) return;

    if (formData.locations.some((loc) => !loc.address || !loc.lat || !loc.lng)) {
      showError('Please fill all location details with valid addresses from suggestions.');
      return;
    }

    const now = new Date();
    const departure = new Date(formData.departureTime);
    const maxFutureDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    if (departure > maxFutureDate) {
      showError('Departure time cannot be more than 30 days in the future.');
      return;
    }

    if (formData.totalWaitingTime < 0) {
      showError('Total waiting time cannot be negative.');
      return;
    }

    if (formData.noOfPassengers < 1) {
      setError('Number of passengers must be at least 1.');
      return;
    }

    if (isServiceManager && (!formData.driverId || !formData.vehicleId)) {
      setError('Please select both a driver and a vehicle.');
      Swal.fire({
        icon: 'error',
        title: 'Missing Selection',
        text: 'Please select both a driver and a vehicle before updating.',
      });
      return;
    }

    const token = checkToken();
    if (!token) return;

    try {
      const updatedFields: Partial<Booking> = {
        reason: formData.reason,
        noOfPassengers: formData.noOfPassengers,
        departureTime: new Date(formData.departureTime).getTime(),
        arrivalTime: new Date(formData.arrivalTime).getTime(),
        bookingType: formData.bookingType,
        priority: formData.priority,
        estimatedKms: formData.estimatedKms,
        totalWaitingTime: formData.totalWaitingTime,
        locations: formData.locations.map(({ id, address, lat, lng, orderId, status, createdAt, type }) => ({
          id,
          address,
          lat,
          lng,
          orderId,
          status: status ?? null,
          createdAt,
          type,
        })),
        ...(isServiceManager && {
          driverId: formData.driverId,
          vehicleId: formData.vehicleId,
        }),
      };

      const success = await updateVehicleBooking(booking.id, updatedFields);
      if (success) {
        showSuccessAlert("Booking updated successfully");
        onHide();
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.statusDescription ||
        error.message ||
        'An unexpected error occurred while updating the booking. Please try again or contact support.';
      setError(errorMessage);
      Swal.fire({
        icon: 'error',
        title: 'Update Error',
        text: errorMessage,
      });
    }
  };

  const canCancelBooking = () => {
    return ['PENDING', 'APPROVED', 'FINAL_APPROVED', 'EXPIRED'].includes(booking.status);
  };

  const handleCancelClick = () => {
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancelSubmit = async () => {
    if (!cancelReason.trim()) {
      showError('Please provide a reason for cancellation.');
      setError('Please provide a reason for cancellation.');
      return;
    }

    showCancelConfirmation(async () => {
      try {
        await apiDefinitions.cancelVehicleBookingRequest(cancelReason, booking.id, booking.groupId || null);
        setShowCancelModal(false);
        setError(null);
        onHide();
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.statusDescription || 'Failed to cancel booking: ' + (err.message || 'Unknown error');
        if (err.response?.status === 401) {
          showError('You are not authorized to cancel this booking. Please check your permissions.');
          setError('You are not authorized to cancel this booking. Please check your permissions.');
        } else if (err.response?.status === 404) {
          showError('Booking not found. It may have been deleted or does not exist.');
          setError('Booking not found. It may have been deleted or does not exist.');
        } else {
          showError(errorMessage);
          setError(errorMessage);
        }
      }
    });
  };

  useEffect(() => {
    autocompleteRefs.current = Array(formData.locations.length).fill(null);
    setFormData({
      bookingType: booking.bookingType || 'Planned',
      priority: booking.priority || 'P1',
      locations: booking.locations?.map((loc, index) => ({
        id: loc.id,
        address: loc.address,
        lat: loc.lat?.toString() || '',
        lng: loc.lng?.toString() || '',
        orderId: loc.orderId || index + 1,
        type: index === 0 ? 'start' : index === booking.locations.length - 1 ? 'return' : 'stop',
        status: loc.status || null,
        createdAt: loc.createdAt || null,
      })) || [
        { id: 0, address: '', lat: '', lng: '', orderId: 1, type: 'start', status: null, createdAt: null },
        { id: 0, address: '', lat: '', lng: '', orderId: 2, type: 'stop', status: null, createdAt: null },
        { id: 0, address: '', lat: '', lng: '', orderId: 3, type: 'return', status: null, createdAt: null },
      ],
      reason: booking.reason || '',
      noOfPassengers: booking.noOfPassengers || 1,
      departureTime: formatDateTimeLocal(booking.departureTime),
      arrivalTime: formatDateTimeLocal(booking.arrivalTime),
      estimatedKms: booking.estimatedKms || 0,
      totalWaitingTime: booking.totalWaitingTime || 0,
      driverId: booking.driverId || '',
      vehicleId: booking.vehicleId || '',
    });
    setStopWaitingTimes({});
    setDirections(null);
    setError(null);
  }, [booking]);

  if (!googleMapsApiKey) {
    return <Alert variant="danger">Error: Google Maps API key not configured</Alert>;
  }

  if (loadError) {
    return <Alert variant="danger">Error loading Google Maps: {loadError.message}</Alert>;
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Booking #{booking.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {(isLoadingDrivers || isLoadingVehicles) && <Alert variant="info">Loading drivers and vehicles...</Alert>}
          {isLoaded ? (
            <Form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Booking Type</Form.Label>
                    <GlassSelect
                      name="bookingType"
                      value={formData.bookingType}
                      onChange={(val) => setFormData(prev => ({ ...prev, bookingType: val }))}
                      options={[
                        { value: 'Planned', label: 'Planned' },
                        { value: 'Sudden', label: 'Sudden' },
                        { value: 'Outstation', label: 'Outstation' },
                      ]}
                      placeholder="Select Booking Type"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <GlassSelect
                      name="priority"
                      value={formData.priority}
                      onChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                      options={[
                        { value: 'P1', label: 'P1 (Max 12 hours)' },
                        { value: 'P2', label: 'P2 (Max 24 hours)' },
                        { value: 'P3', label: 'P3 (Max 48 hours)' },
                      ]}
                      placeholder="Select Priority"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Reason</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="reason"
                      placeholder="Reason for booking"
                      value={formData.reason}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Number of Passengers</Form.Label>
                    <Form.Control
                      type="number"
                      name="noOfPassengers"
                      placeholder="Number of Passengers"
                      value={formData.noOfPassengers}
                      onChange={handleInputChange}
                      min="1"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Departure Time</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      name="departureTime"
                      value={formData.departureTime}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Arrival Time</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      name="arrivalTime"
                      value={formData.arrivalTime}
                      onChange={handleInputChange}
                    />
                    {formData.arrivalTime && (
                      <Form.Text className="text-muted">
                        Arrival time is auto-calculated based on route, departure time, and waiting times. You can manually adjust it if needed.
                      </Form.Text>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Estimated KMs</Form.Label>
                    <div className="form-control-plaintext" style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                      {formData.estimatedKms > 0 ? `${formData.estimatedKms} Km` : <span className="text-muted">0</span>}
                    </div>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Total Waiting Time</Form.Label>
                    <div className="form-control-plaintext" style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                      {formData.totalWaitingTime} minutes
                    </div>
                  </Form.Group>
                  {isServiceManager && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Driver</Form.Label>
                        <GlassSelect
                          value={formData.driverId || ''}
                          onChange={(val) => handleServiceManagerInputChange('driverId', val)}
                          disabled={isLoadingDrivers}
                          options={[
                            { value: '', label: 'Select Driver' },
                            ...drivers.map((driver) => ({
                              value: driver.empNumber,
                              label: `${driver.name} (${driver.empNumber})`,
                            })),
                          ]}
                          placeholder="Select Driver"
                          searchable={drivers.length > 5}
                        />
                        {drivers.length === 0 && !isLoadingDrivers && (
                          <Form.Text className="text-danger">No drivers available.</Form.Text>
                        )}
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Vehicle</Form.Label>
                        <GlassSelect
                          value={formData.vehicleId || ''}
                          onChange={(val) => handleServiceManagerInputChange('vehicleId', val)}
                          disabled={isLoadingVehicles}
                          options={[
                            { value: '', label: 'Select Vehicle' },
                            ...vehicles.map((vehicle) => ({
                              value: vehicle.numberPlate,
                              label: `${vehicle.noOfPassengers}PA (${vehicle.numberPlate})`,
                            })),
                          ]}
                          placeholder="Select Vehicle"
                          searchable={vehicles.length > 5}
                        />
                        {vehicles.length === 0 && !isLoadingVehicles && (
                          <Form.Text className="text-danger">No vehicles available.</Form.Text>
                        )}
                      </Form.Group>
                    </>
                  )}
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Locations</Form.Label>
                        {formData.locations.map((location, index) => (
                          <div className="row mb-2 align-items-end" key={index}>
                            <div className={location.type === 'stop' ? 'col-md-6' : 'col-md-8'}>
                              <Form.Label>
                                {location.type.charAt(0).toUpperCase() + location.type.slice(1)} Location
                              </Form.Label>

                              {locationsLocked ? (
                                // 🔒 View-only: plain disabled input (no Autocomplete)
                                <input
                                  type="text"
                                  className="form-control"
                                  value={location.address}
                                  disabled
                                  readOnly
                                  // Prevent pointer/focus interactions visually
                                  style={{ pointerEvents: 'none', backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                  tabIndex={-1}
                                />
                              ) : (
                                // ✏️ Editable: modern Places API (New) Autocomplete
                                <ModernPlaceAutocomplete
                                  value={location.address}
                                  placeholder={`Search ${location.type} address`}
                                  onChange={(val) => handleLocationChange(index, 'address', val)}
                                  onPlaceSelect={(address, lat, lng) => {
                                    setFormData((prev) => {
                                      const newLocations = [...prev.locations];
                                      newLocations[index] = { ...newLocations[index], address, lat, lng };
                                      return { ...prev, locations: newLocations };
                                    });
                                    setDirections(null);
                                  }}
                                />
                              )}
                            </div>

                            {location.type === 'stop' && (
                              <div className="col-md-4">
                                <Form.Label>Waiting Time (minutes)</Form.Label>
                                <Form.Control
                                  type="number"
                                  placeholder="Waiting time"
                                  value={stopWaitingTimes[index] || 0}
                                  onChange={(e) => handleWaitingTimeChange(index, Number(e.target.value))}
                                  min="0"
                                  disabled={locationsLocked}                  // 🔒 disable waiting time
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </Form.Group>
                  <div className="mb-3">
                    <GoogleMap
                      mapContainerStyle={{ height: '300px', width: '100%' }}
                      center={center}
                      zoom={12}
                      onLoad={(map: google.maps.Map) => { mapRef.current = map; }}
                      options={locationsLocked ? {
                        draggable: false,
                        scrollwheel: false,
                        disableDoubleClickZoom: true,
                        draggableCursor: 'default',
                        gestureHandling: 'none',
                      } : undefined}
                    >
                      {formData.locations.map((location, index) => (
                        location.lat && location.lng && (
                          <Marker
                            key={index}
                            position={{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }}
                            title={location.type.charAt(0).toUpperCase() + location.type.slice(1)}
                          />
                        )
                      ))}
                      {routePath.length > 0 && (
                        <Polyline
                          path={routePath}
                          options={{
                            strokeColor: '#2563EB',
                            strokeOpacity: 0.9,
                            strokeWeight: 5,
                          }}
                        />
                      )}
                      {directions && (
                        <DirectionsRenderer
                          directions={directions}
                          options={{
                            polylineOptions: {
                              strokeColor: '#2563EB',
                              strokeOpacity: 0.8,
                              strokeWeight: 5,
                            },
                          }}
                        />
                      )}
                    </GoogleMap>
                  </div>
                </div>
                <div className="col-md-12">
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isUpdating || isLoadingDrivers || isLoadingVehicles}
                  >
                    {isUpdating ? 'Updating...' : 'Update Booking'}
                  </Button>
                </div>
              </div>
            </Form>
          ) : (
            <Alert variant="info">Loading Google Maps...</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={isUpdating || isLoadingDrivers || isLoadingVehicles}
          >
            Close
          </Button>
          {canCancelBooking() && (
            <Button
              variant="danger"
              onClick={handleCancelClick}
              disabled={isUpdating || isLoadingDrivers || isLoadingVehicles}
            >
              Cancel Booking
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Booking #{booking.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Reason for Cancellation</Form.Label>
            <Form.Control
              type="text"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter reason for cancellation"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Close
          </Button>
          <Button
            variant="danger"
            onClick={handleCancelSubmit}
            disabled={!cancelReason.trim()}
          >
            Confirm Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditVehicleBookings;